// Scheduled Email Subscriptions — managers can subscribe to a
// "Morning Revivable Deals" daily digest (or weekdays-only). Mounted
// on the Admin Overview page as a collapsible card so it doesn't
// dominate the page for users who haven't set anything up yet.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CalendarClock, Plus, Trash2, Send, Loader2, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_OPTIONS = [
  { value: 'daily', label: 'Every day' },
  { value: 'weekdays', label: 'Weekdays (Mon–Fri)' },
  { value: 0, label: 'Every Monday' },
  { value: 1, label: 'Every Tuesday' },
  { value: 2, label: 'Every Wednesday' },
  { value: 3, label: 'Every Thursday' },
  { value: 4, label: 'Every Friday' },
  { value: 5, label: 'Every Saturday' },
  { value: 6, label: 'Every Sunday' },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

const dayLabel = (dow) => {
  const opt = DAY_OPTIONS.find((o) => String(o.value) === String(dow));
  return opt ? opt.label : String(dow);
};

const fmtTime = (h, m) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
const fmtRelative = (iso) => {
  if (!iso) return 'never';
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const mins = Math.round(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const h = Math.round(mins / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch {
    return iso;
  }
};

export const SubscriptionsCard = ({ defaultDomainId = null }) => {
  const { token, user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  // Draft form for creating a new subscription
  const [form, setForm] = useState({
    domain_id: defaultDomainId || '',
    recipients: '',
    hour: 8,
    minute: 0,
    day_of_week: 'weekdays',
    label: 'Morning Revivable Deals',
    limit: 15,
  });
  const [creating, setCreating] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const isMaster = user?.role === 'master_admin';

  const fetchAll = () => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      })
      .then((r) => setSubs(r.data?.subscriptions || []))
      .catch(() => setSubs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (expanded && subs.length === 0) fetchAll();
  }, [expanded, subs.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Master admins pick a target domain; non-master users are locked to their own.
  useEffect(() => {
    if (!expanded || !isMaster) return;
    axios
      .get(`${API}/admin/sync-status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      })
      .then((r) => setDomains(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDomains([]));
  }, [expanded, isMaster, token]);

  const domainNameById = useMemo(() => {
    const m = {};
    for (const d of domains) m[d.id] = d.company_name || d.name || d.id;
    return m;
  }, [domains]);

  const createSubscription = async () => {
    const recipients = form.recipients
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(isEmail);
    if (recipients.length === 0) {
      toast.error('At least one valid recipient email required');
      return;
    }
    const body = {
      kind: 'revivable_deals',
      recipients,
      hour: Number(form.hour),
      minute: Number(form.minute),
      day_of_week: form.day_of_week,
      label: form.label,
      limit: Number(form.limit),
    };
    if (isMaster && form.domain_id) body.domain_id = form.domain_id;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/subscriptions`, body, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      setSubs((prev) => [r.data, ...prev]);
      toast.success(`Scheduled "${r.data.label}" — fires ${dayLabel(r.data.day_of_week)} at ${fmtTime(r.data.hour, r.data.minute)}`);
      setForm((f) => ({ ...f, recipients: '' }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create subscription');
    } finally {
      setCreating(false);
    }
  };

  const toggleEnabled = async (sub) => {
    try {
      const r = await axios.patch(
        `${API}/subscriptions/${sub.id}`,
        { enabled: !sub.enabled },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? r.data : s)));
    } catch {
      toast.error('Failed to update subscription');
    }
  };

  const deleteSubscription = async (sub) => {
    try {
      await axios.delete(`${API}/subscriptions/${sub.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setSubs((prev) => prev.filter((s) => s.id !== sub.id));
      toast.success(`Deleted "${sub.label}"`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const testSend = async (sub) => {
    setTestingId(sub.id);
    try {
      const r = await axios.post(
        `${API}/subscriptions/${sub.id}/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      if (r.data?.sent) {
        toast.success(`Test sent — ${r.data.deal_count} deal${r.data.deal_count === 1 ? '' : 's'} in the digest`);
      } else {
        toast.info(`Not sent: ${r.data?.reason || 'unknown reason'}`);
      }
      setSubs((prev) =>
        prev.map((s) =>
          s.id === sub.id ? { ...s, last_sent_at: new Date().toISOString(), last_send_result: r.data } : s,
        ),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div
      className="bg-white/5 border border-white/10 rounded-xl"
      data-testid="subscriptions-card"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] rounded-xl"
        data-testid="subscriptions-toggle"
      >
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-teal-400" />
          <h3 className="text-sm font-semibold text-white">Scheduled Email Subscriptions</h3>
          {subs.length > 0 && (
            <span className="text-xs bg-teal-500/20 text-teal-300 px-2 py-0.5 rounded-full font-semibold">
              {subs.length}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-white/40 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4">
          <p className="text-xs text-white/50">
            Get a daily or weekly digest of the top revivable deals in your inbox — perfect for morning stand-ups.
          </p>

          {/* Existing subscriptions */}
          {loading ? (
            <div className="py-6 flex items-center justify-center">
              <Loader2 className="w-5 h-5 text-white/30 animate-spin" />
            </div>
          ) : subs.length === 0 ? (
            <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-lg py-4 px-3 text-center">
              <p className="text-sm text-white/50">No subscriptions yet — add one below.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="subscriptions-list">
              {subs.map((sub) => (
                <div
                  key={sub.id}
                  className="bg-white/[0.02] rounded-lg p-3 flex items-center gap-3"
                  data-testid={`subscription-row-${sub.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-white truncate">{sub.label}</p>
                      {!sub.enabled && (
                        <span className="text-[10px] uppercase font-bold text-white/40 bg-white/5 px-1.5 py-0.5 rounded">
                          Paused
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/50 mt-0.5">
                      {dayLabel(sub.day_of_week)} · {fmtTime(sub.hour, sub.minute)} {sub.timezone}
                      {' · '}
                      {sub.recipients?.length || 0} recipient{sub.recipients?.length === 1 ? '' : 's'}
                      {isMaster && domainNameById[sub.domain_id] && ` · ${domainNameById[sub.domain_id]}`}
                    </p>
                    <p className="text-[11px] text-white/40 mt-0.5">
                      Last sent {fmtRelative(sub.last_sent_at)}
                      {sub.last_send_result?.deal_count != null && ` · ${sub.last_send_result.deal_count} deals`}
                    </p>
                  </div>
                  <button
                    onClick={() => testSend(sub)}
                    disabled={testingId === sub.id}
                    className="text-xs font-semibold text-teal-300 hover:text-teal-200 inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`subscription-test-${sub.id}`}
                    title="Send now to test"
                  >
                    {testingId === sub.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Send className="w-3 h-3" />
                    )}
                    Test
                  </button>
                  <button
                    onClick={() => toggleEnabled(sub)}
                    className="text-xs font-semibold text-white/60 hover:text-white"
                    data-testid={`subscription-toggle-${sub.id}`}
                  >
                    {sub.enabled ? 'Pause' : 'Resume'}
                  </button>
                  <button
                    onClick={() => deleteSubscription(sub)}
                    className="text-white/40 hover:text-rose-400 p-1"
                    data-testid={`subscription-delete-${sub.id}`}
                    aria-label={`Delete ${sub.label}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Create form */}
          <div className="border-t border-white/10 pt-4">
            <p className="text-[11px] uppercase tracking-wider text-white/40 font-semibold mb-2">
              Add a new subscription
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {isMaster && (
                <select
                  value={form.domain_id}
                  onChange={(e) => setForm((f) => ({ ...f, domain_id: e.target.value }))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white col-span-2"
                  data-testid="subscription-form-domain"
                >
                  <option value="">Select a domain…</option>
                  {domains.map((d) => (
                    <option key={d.domain_id || d.id} value={d.domain_id || d.id} className="text-slate-900">
                      {d.company_name || d.name || d.domain_id || d.id}
                    </option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value.slice(0, 80) }))}
                placeholder="Label (e.g. Morning digest)"
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white placeholder:text-white/30"
                data-testid="subscription-form-label"
              />
              <select
                value={form.day_of_week}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    day_of_week: v === 'daily' || v === 'weekdays' ? v : Number(v),
                  }));
                }}
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                data-testid="subscription-form-dow"
              >
                {DAY_OPTIONS.map((o) => (
                  <option key={String(o.value)} value={o.value} className="text-slate-900">
                    {o.label}
                  </option>
                ))}
              </select>
              <div className="flex items-center gap-1.5">
                <select
                  value={form.hour}
                  onChange={(e) => setForm((f) => ({ ...f, hour: Number(e.target.value) }))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                  data-testid="subscription-form-hour"
                >
                  {HOUR_OPTIONS.map((h) => (
                    <option key={h} value={h} className="text-slate-900">
                      {String(h).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className="text-white/40 text-sm">:</span>
                <select
                  value={form.minute}
                  onChange={(e) => setForm((f) => ({ ...f, minute: Number(e.target.value) }))}
                  className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                  data-testid="subscription-form-minute"
                >
                  {[0, 15, 30, 45].map((m) => (
                    <option key={m} value={m} className="text-slate-900">
                      {String(m).padStart(2, '0')}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-white/40">UTC</span>
              </div>
              <input
                type="number"
                min={1}
                max={50}
                value={form.limit}
                onChange={(e) => setForm((f) => ({ ...f, limit: Math.max(1, Math.min(50, Number(e.target.value) || 15)) }))}
                placeholder="Deals per digest"
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white"
                data-testid="subscription-form-limit"
              />
              <input
                type="text"
                value={form.recipients}
                onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
                placeholder="Recipients (comma-separated emails)"
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm text-white placeholder:text-white/30 col-span-2"
                data-testid="subscription-form-recipients"
              />
            </div>
            <button
              onClick={createSubscription}
              disabled={creating || (isMaster && !form.domain_id) || !form.recipients.trim()}
              className="mt-3 inline-flex items-center gap-1.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white text-sm font-semibold px-3 py-1.5 rounded-md"
              data-testid="subscription-form-submit"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add subscription
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsCard;
